import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErrorMessage, formatCurrency, formatDate, formatDateShort } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import ReceivePaymentDialog from "@/components/ReceivePaymentDialog";
import EditOrderDialog from "@/components/EditOrderDialog";
import { buildWhatsAppUrl, invoiceShareMessage } from "@/lib/whatsapp";
import {
    CaretLeft, CheckCircle, Circle, Package, Truck, MapPin, Coins,
    FileText, WhatsappLogo, ClockCounterClockwise, PackageIcon, ArrowRight,
    PencilSimple,
} from "@phosphor-icons/react";

const STAGES = [
    { key: "new",        label: "Received",  Icon: Package },
    { key: "confirmed",  label: "Confirmed", Icon: CheckCircle },
    { key: "packed",     label: "Packed",    Icon: Package },
    { key: "dispatched", label: "Dispatched", Icon: Truck },
    { key: "delivered",  label: "Delivered", Icon: MapPin },
];

export default function OrderDetail() {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dispatchOpen, setDispatchOpen] = useState(false);
    const [paymentOpen, setPaymentOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/sales/${orderId}`);
            setOrder(data);
        } catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId]);

    const advance = async (stage, extra = {}) => {
        try {
            await api.patch(`/sales/${orderId}/fulfillment`, { stage, ...extra });
            toast.success(`Order marked ${stage}`);
            load();
        } catch (e) { toast.error(apiErrorMessage(e)); }
    };

    if (loading || !order) {
        return <div className="p-8 text-sm text-neutral-500" data-testid="order-loading">Loading order...</div>;
    }

    const currentStageIdx = STAGES.findIndex((s) => s.key === (order.fulfillment_stage || "new"));
    const isCancelled = order.status === "cancelled";
    const c = order.customer_snapshot || {};

    const shareWhatsApp = () => {
        const msg = invoiceShareMessage({
            customerName: order.customer_name,
            invoiceNo: order.order_no,
            total: order.total,
            invoiceUrl: `${window.location.origin}/invoice/${order.id}`,
        });
        window.open(buildWhatsAppUrl(c.whatsapp || c.mobile, msg), "_blank");
    };

    return (
        <div className="space-y-6" data-testid="order-detail-page">
            <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900" data-testid="order-back">
                <CaretLeft size={16} /> Back
            </button>

            {/* Header */}
            <div className="bg-white border border-neutral-200 rounded-md p-5 flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 min-w-0">
                    <div className="eyebrow mb-1">Order</div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <h1 className="text-3xl font-extrabold font-mono tracking-tight">{order.order_no}</h1>
                        <StatusBadge status={order.status} />
                        <PaymentBadge status={order.payment_status} />
                    </div>
                    <div className="text-xs text-neutral-500 mt-2">
                        Created {formatDate(order.created_at)} by <b className="text-neutral-800">{order.created_by_name}</b>
                        {order.completed_at && <> · Completed {formatDate(order.completed_at)}</>}
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {order.status !== "completed" && order.status !== "cancelled" && (
                        <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="order-edit-btn">
                            <PencilSimple size={14} className="mr-1.5" /> Edit Order
                        </Button>
                    )}
                    <Button variant="outline" onClick={() => navigate(`/invoice/${order.id}`)} data-testid="order-open-invoice">
                        <FileText size={14} className="mr-1.5" /> Invoice
                    </Button>
                    <Button variant="outline" onClick={shareWhatsApp} data-testid="order-share-whatsapp">
                        <WhatsappLogo size={14} weight="fill" className="mr-1.5" style={{ color: "#25D366" }} /> WhatsApp
                    </Button>
                    {order.payment_status !== "paid" && order.status !== "cancelled" && (
                        <Button onClick={() => setPaymentOpen(true)} className="text-white" style={{ backgroundColor: "#15803D" }} data-testid="order-receive-payment">
                            <Coins size={14} className="mr-1.5" /> Receive Payment
                        </Button>
                    )}
                </div>
            </div>

            {/* Lifecycle stepper */}
            <div className="bg-white border border-neutral-200 rounded-md p-5" data-testid="order-lifecycle">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="eyebrow">Fulfillment</div>
                        <h3 className="text-lg font-bold font-display mt-1">Order Lifecycle</h3>
                    </div>
                    {!isCancelled && currentStageIdx < STAGES.length - 1 && (
                        <div className="flex gap-2">
                            {order.fulfillment_stage !== "dispatched" && (
                                <Button
                                    onClick={() => {
                                        const nextStage = STAGES[currentStageIdx + 1].key;
                                        if (nextStage === "dispatched") setDispatchOpen(true);
                                        else advance(nextStage);
                                    }}
                                    className="text-white"
                                    style={{ backgroundColor: "#15803D" }}
                                    data-testid="order-advance-stage"
                                >
                                    Mark {STAGES[currentStageIdx + 1].label} <ArrowRight size={14} className="ml-1.5" />
                                </Button>
                            )}
                            {order.fulfillment_stage === "dispatched" && (
                                <Button onClick={() => advance("delivered")} className="text-white" style={{ backgroundColor: "#15803D" }} data-testid="order-mark-delivered">
                                    Mark Delivered <ArrowRight size={14} className="ml-1.5" />
                                </Button>
                            )}
                        </div>
                    )}
                </div>

                {/* Stepper */}
                <div className="flex items-center justify-between gap-2 overflow-x-auto pb-2">
                    {STAGES.map((s, i) => {
                        const done = !isCancelled && i <= currentStageIdx;
                        const active = !isCancelled && i === currentStageIdx;
                        return (
                            <React.Fragment key={s.key}>
                                <div className="flex flex-col items-center min-w-[80px]" data-testid={`stage-${s.key}`}>
                                    <div className={"w-10 h-10 rounded-full flex items-center justify-center " + (done ? "text-white" : "text-neutral-400 bg-neutral-100")}
                                         style={done ? { backgroundColor: active ? "#15803D" : "#22C55E" } : {}}>
                                        <s.Icon size={18} weight="bold" />
                                    </div>
                                    <div className={"text-[11px] font-semibold mt-1.5 " + (done ? "text-neutral-900" : "text-neutral-400")}>
                                        {s.label}
                                    </div>
                                </div>
                                {i < STAGES.length - 1 && (
                                    <div className={"flex-1 h-0.5 " + (i < currentStageIdx && !isCancelled ? "bg-green-500" : "bg-neutral-200")}></div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>

                {isCancelled && (
                    <div className="mt-4 bg-red-50 border border-red-200 text-red-700 text-sm font-semibold rounded p-3 text-center">
                        This order has been cancelled.
                    </div>
                )}

                {/* Dispatch info */}
                {order.dispatch && (
                    <div className="mt-5 pt-4 border-t border-neutral-200 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <InfoBlock label="Courier" value={order.dispatch.courier || "—"} />
                        <InfoBlock label="Tracking No." value={order.dispatch.tracking_no || "—"} mono />
                        <InfoBlock label="Expected Delivery" value={order.dispatch.expected_delivery ? formatDateShort(order.dispatch.expected_delivery) : "—"} />
                        <InfoBlock label="Dispatched By" value={order.dispatch.dispatched_by_name || "—"} />
                    </div>
                )}
            </div>

            {/* Customer snapshot + Items */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="bg-white border border-neutral-200 rounded-md p-5" data-testid="order-customer-snapshot">
                    <div className="eyebrow">Bill To</div>
                    <h3 className="text-lg font-bold font-display mt-1 mb-3">{c.name || order.customer_name}</h3>
                    <div className="space-y-2 text-sm">
                        <SnapshotLine label="Customer ID" value={c.id ? c.id.slice(0, 8).toUpperCase() : "—"} mono />
                        <SnapshotLine label="Mobile" value={c.mobile || "—"} />
                        <SnapshotLine label="WhatsApp" value={c.whatsapp || c.mobile || "—"} />
                        <SnapshotLine label="Email" value={c.email || "—"} />
                        <SnapshotLine label="GST" value={c.gst_number || "—"} mono />
                        <SnapshotLine label="Address" value={c.address || order.delivery_address || "—"} />
                    </div>
                </div>

                <div className="lg:col-span-2 bg-white border border-neutral-200 rounded-md p-5">
                    <div className="eyebrow">Items ({order.items.length})</div>
                    <div className="mt-2 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
                                <tr>
                                    <th className="text-left py-2 font-semibold">Product</th>
                                    <th className="text-right py-2 font-semibold">Qty</th>
                                    <th className="text-right py-2 font-semibold">Rate</th>
                                    <th className="text-right py-2 font-semibold">GST</th>
                                    <th className="text-right py-2 font-semibold">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {order.items.map((it, i) => (
                                    <tr key={i} className="border-b border-neutral-100">
                                        <td className="py-2">
                                            <div className="font-medium">{it.product_name}</div>
                                            <div className="text-[10px] font-mono text-neutral-500">{it.sku}</div>
                                        </td>
                                        <td className="text-right py-2 tabular">{it.quantity}</td>
                                        <td className="text-right py-2 tabular">{formatCurrency(it.unit_price)}</td>
                                        <td className="text-right py-2 tabular">{it.gst_percent}%</td>
                                        <td className="text-right py-2 tabular font-semibold">{formatCurrency(it.line_total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-3 pt-3 border-t border-neutral-200 flex justify-end">
                        <div className="w-full max-w-[220px] text-sm space-y-1">
                            <div className="flex justify-between"><span className="text-neutral-500">Subtotal</span><span className="tabular">{formatCurrency(order.subtotal)}</span></div>
                            <div className="flex justify-between"><span className="text-neutral-500">GST</span><span className="tabular">{formatCurrency(order.tax)}</span></div>
                            <div className="flex justify-between"><span className="text-neutral-500">Discount</span><span className="tabular">-{formatCurrency(order.discount)}</span></div>
                            <div className="flex justify-between font-extrabold font-display text-lg border-t border-neutral-200 pt-2 mt-1">
                                <span>Total</span><span className="tabular" style={{ color: "#15803D" }}>{formatCurrency(order.total)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Timeline */}
            <div className="bg-white border border-neutral-200 rounded-md p-5" data-testid="order-timeline">
                <div className="eyebrow">Audit trail</div>
                <h3 className="text-lg font-bold font-display mt-1 mb-4 flex items-center gap-2">
                    <ClockCounterClockwise size={18} weight="duotone" /> Timeline
                </h3>
                <div className="space-y-4">
                    {(order.fulfillment_history || []).slice().reverse().map((h, i) => {
                        const stage = STAGES.find((s) => s.key === h.stage);
                        return (
                            <div key={i} className="flex gap-3">
                                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                                     style={{ background: "#DCFCE7", color: "#15803D" }}>
                                    {stage ? <stage.Icon size={14} weight="bold" /> : <Circle size={14} />}
                                </div>
                                <div className="flex-1 pb-4 border-b border-neutral-100 last:border-b-0 last:pb-0">
                                    <div className="text-sm font-semibold">
                                        {stage?.label || h.stage} <span className="text-neutral-500 font-normal">· by {h.user_name}</span>
                                    </div>
                                    <div className="text-xs text-neutral-500 mt-0.5">{formatDate(h.timestamp)}</div>
                                    {h.notes && <div className="text-xs text-neutral-600 mt-1 italic">{h.notes}</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Dispatch dialog */}
            <DispatchDialog
                open={dispatchOpen}
                onOpenChange={setDispatchOpen}
                onSubmit={(vals) => { advance("dispatched", vals); setDispatchOpen(false); }}
            />

            <ReceivePaymentDialog
                open={paymentOpen}
                onOpenChange={setPaymentOpen}
                defaultSaleId={order.id}
                defaultCustomerId={order.customer_id || ""}
                defaultInvoiceNo={order.order_no}
                onSaved={() => { setPaymentOpen(false); load(); }}
            />

            <EditOrderDialog
                open={editOpen}
                onOpenChange={setEditOpen}
                order={order}
                onSaved={() => { setEditOpen(false); load(); }}
            />
        </div>
    );
}

function DispatchDialog({ open, onOpenChange, onSubmit }) {
    const [courier, setCourier] = useState("");
    const [tracking, setTracking] = useState("");
    const [expected, setExpected] = useState("");
    const [notes, setNotes] = useState("");

    useEffect(() => {
        if (open) { setCourier(""); setTracking(""); setExpected(""); setNotes(""); }
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="dispatch-dialog">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-extrabold font-display">Dispatch Order</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Courier / Transport</Label>
                        <Input value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="e.g. Delhivery, Own Vehicle" className="mt-1.5" data-testid="dispatch-courier" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Tracking / AWB No.</Label>
                        <Input value={tracking} onChange={(e) => setTracking(e.target.value)} className="mt-1.5" data-testid="dispatch-tracking" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Expected Delivery Date</Label>
                        <Input type="date" value={expected} onChange={(e) => setExpected(e.target.value)} className="mt-1.5" data-testid="dispatch-expected" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Notes</Label>
                        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1.5" data-testid="dispatch-notes" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dispatch-cancel">Cancel</Button>
                    <Button
                        onClick={() => onSubmit({ courier, tracking_no: tracking, expected_delivery: expected ? new Date(expected).toISOString() : "", notes })}
                        className="text-white" style={{ backgroundColor: "#15803D" }}
                        data-testid="dispatch-confirm"
                    >
                        Confirm Dispatch
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function InfoBlock({ label, value, mono }) {
    return (
        <div>
            <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold mb-1">{label}</div>
            <div className={"text-sm font-semibold text-neutral-900 " + (mono ? "font-mono" : "")}>{value}</div>
        </div>
    );
}

function SnapshotLine({ label, value, mono }) {
    return (
        <div className="flex items-start gap-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold w-24 shrink-0 pt-0.5">{label}</div>
            <div className={"text-sm text-neutral-800 flex-1 break-words " + (mono ? "font-mono" : "")}>{value}</div>
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        draft:     { bg: "#F3F4F6", fg: "#525252" },
        pending:   { bg: "#FEF3C7", fg: "#B45309" },
        completed: { bg: "#DCFCE7", fg: "#15803D" },
        cancelled: { bg: "#FEE2E2", fg: "#DC2626" },
    };
    const s = map[status] || { bg: "#F3F4F6", fg: "#525252" };
    return <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded" style={{ background: s.bg, color: s.fg }}>{status}</span>;
}

function PaymentBadge({ status }) {
    const map = {
        unpaid:  { bg: "#FEE2E2", fg: "#DC2626" },
        partial: { bg: "#FEF3C7", fg: "#B45309" },
        paid:    { bg: "#DCFCE7", fg: "#15803D" },
    };
    const s = map[status] || { bg: "#F3F4F6", fg: "#525252" };
    return <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded" style={{ background: s.bg, color: s.fg }}>{status}</span>;
}
